import { Box, Stack, Tooltip, Typography, useTheme } from "@mui/material";
import { WasteManifestSource } from "generated-graphql/graphql";
import { prettyPrintDaysFromInt } from "util/dates";

const SOURCE_ORDER: readonly WasteManifestSource[] = [
  WasteManifestSource.EManifest,
  WasteManifestSource.Vendor,
  WasteManifestSource.ManifestDocument,
];

const SOURCE_LETTER: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "E",
  [WasteManifestSource.Vendor]: "V",
  [WasteManifestSource.ManifestDocument]: "D",
};

const SOURCE_LABEL: Record<WasteManifestSource, string> = {
  [WasteManifestSource.EManifest]: "e-Manifest",
  [WasteManifestSource.Vendor]: "Vendor",
  [WasteManifestSource.ManifestDocument]: "Document",
};

export interface SourcesCellProps {
  availableSources: readonly WasteManifestSource[];
  /**
   * Days since shipment with no e-Manifest record yet. Shown inline next to a
   * missing e-Manifest (e.g. "e-Manifest: missing (52 days)") so this column
   * subsumes the standalone "Days awaiting e-Manifest" column.
   */
  daysAwaitingEManifest?: number | null;
  /**
   * Descriptive document processing status (e.g. "reviewed", "needs review",
   * "import failed"). When provided and the Document source is present, the
   * tooltip shows it in place of "present" (e.g. "Document: reviewed"), letting
   * this column subsume the standalone Document column. `null`/undefined falls
   * back to present/missing.
   */
  documentStatusLabel?: string | null;
}

/**
 * Three small pills (`E` / `V` / `D`) stating which data pillars are on file
 * for a given manifest. The tooltip lists each source as present/missing and,
 * when the e-Manifest is missing, how many days it has been awaited.
 *
 * Why three letters instead of full words: the pills need to land in a 38px
 * grid row alongside the manifest tracking# and facility, and the alphabet
 * column is repeated identically on every row so a glance trains the user
 * fast.
 */
export function SourcesCell({
  availableSources,
  daysAwaitingEManifest,
  documentStatusLabel,
}: SourcesCellProps) {
  const theme = useTheme();
  const present = new Set(availableSources);

  const tooltipLines: string[] = SOURCE_ORDER.map((source) => {
    const isPresent = present.has(source);

    // Document line surfaces the processing status (reviewed / needs review /
    // import failed / attached) in place of a bare "present".
    if (source === WasteManifestSource.ManifestDocument && isPresent && documentStatusLabel) {
      return `${SOURCE_LABEL[source]}: ${documentStatusLabel}`;
    }

    const status = isPresent ? "present" : "missing";
    const line = `${SOURCE_LABEL[source]}: ${status}`;
    if (
      source === WasteManifestSource.EManifest &&
      status === "missing" &&
      daysAwaitingEManifest != null
    ) {
      return `${line} (${prettyPrintDaysFromInt(daysAwaitingEManifest)})`;
    }
    return line;
  });

  return (
    <Tooltip
      title={
        <Stack spacing={0.25}>
          {tooltipLines.map((line) => (
            <Typography key={line} variant="caption" sx={{ fontSize: 11 }}>
              {line}
            </Typography>
          ))}
        </Stack>
      }
      placement="right"
      enterDelay={400}
    >
      <Stack direction="row" spacing={0.5} alignItems="center">
        {SOURCE_ORDER.map((source) => {
          const isPresent = present.has(source);

          // All source pills share the e-Manifest green so the column reads as
          // a single availability scale rather than a per-source color code.
          const palette = theme.palette.encampGreen;

          const bg = isPresent ? palette.light : "transparent";
          const fg = isPresent ? palette.dark : theme.palette.text.disabled;
          const border = isPresent ? palette.light : theme.palette.divider;

          return (
            <Box
              key={source}
              aria-label={`${SOURCE_LABEL[source]}: ${isPresent ? "present" : "missing"}`}
              sx={{
                width: 20,
                height: 20,
                borderRadius: 0.75,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 11,
                fontWeight: 500,
                backgroundColor: bg,
                color: fg,
                border: `1px solid ${border}`,
              }}
            >
              {SOURCE_LETTER[source]}
            </Box>
          );
        })}
      </Stack>
    </Tooltip>
  );
}
